<script setup>
import { computed } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useMobileConversationUi } from 'dashboard/composables/useMobileConversationUi';
import { isSmallScreenWidth } from 'dashboard/components/widgets/conversation/mobile/mobileConversationChrome';
import { WORKBENCH_CONTACT_PANEL_CARD_CLASS } from 'dashboard/components/conversationColumnLayout';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { isWorkspaceV2 } = useWorkspaceV2();
const { rightDragWidth } = useWorkbenchLayout();
const { width: windowWidth } = useWindowSize();
const { mobileContactOpen, closeMobileContact } = useMobileConversationUi();

const isSmallScreen = computed(() => isSmallScreenWidth(windowWidth.value));

const activeTab = computed(() => {
  // SiteUp: no celular o painel nao herda o is_contact_sidebar_open do desktop.
  // Abrir a conversa cai na conversa; o menu "ver contato" liga mobileContactOpen.
  if (isSmallScreen.value) {
    return mobileContactOpen.value ? 0 : null;
  }
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;
  if (isContactSidebarOpen) {
    return 0;
  }
  return null;
});

const closeContactPanel = () => {
  if (isSmallScreen.value) {
    closeMobileContact();
    return;
  }
  // V2 keeps contact context visible while the attendant writes or resizes.
  if (isWorkspaceV2.value) return;
  if (uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
    });
  }
};
</script>

<template>
  <div
    v-on-click-outside="[
      () => closeContactPanel(),
      {
        ignore: [
          'dialog[open]',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="conv-sidebar-collapse overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:h-auto md:min-h-0 md:w-auto md:max-w-none"
    :class="[
      {
        'conv-sidebar-collapse--closed':
          activeTab !== 0 && rightDragWidth == null,
      },
      isSmallScreen
        ? 'h-full bg-n-background ltr:border-l rtl:border-r border-n-weak shadow-lg'
        : WORKBENCH_CONTACT_PANEL_CARD_CLASS,
    ]"
    :style="
      rightDragWidth != null ? { width: `${rightDragWidth}px` } : undefined
    "
  >
    <div
      class="conv-sidebar-collapse__inner flex-1 min-h-0"
      :class="
        rightDragWidth != null
          ? 'w-full min-w-0'
          : 'md:w-[320px] md:min-w-[320px] 2xl:w-[360px] 2xl:min-w-[360px]'
      "
    >
      <div class="flex flex-1 overflow-auto">
        <ContactPanel
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* SiteUp — colapso do painel de contato.
 *
 * A ANIMAÇÃO DE LARGURA FOI REMOVIDA (2026-08-10) depois de 3 tentativas e de
 * quebrar o fechamento em produção. O histórico, pra ninguém tentar de novo do
 * mesmo jeito:
 *
 *  1. `grid-template-columns: 1fr → 0fr` no elemento externo. NÃO fecha: o
 *     item de grid tinha `md:min-w-[320px]`, e min-width segura a track.
 *  2. Zerar o min-width do item. Min-width virou 0 (confirmado no computed) e
 *     a track continuou 320px: `0fr` distribui só o espaço LIVRE, e uma
 *     `width` DEFINIDA no item é base da track, não espaço livre.
 *  3. Mover a largura pro filho. Fechou, mas parou de ABRIR: sem largura no
 *     item, o container `w-auto` não tem o que distribuir e `1fr` resolve 0.
 *  4. `width: 0` no externo com transição. Ignorado: o elemento é item de FLEX
 *     e o pai o dimensiona pelo conteúdo.
 *
 * O que funciona e está medido no staging: `display: none` (fechado 0px,
 * aberto 321px). Correção > animação: um painel que não fecha é bug de
 * produto; a transição era enfeite.
 *
 * Se alguém quiser a animação de volta, o caminho provável é animar
 * `transform: translateX(100%)` com o painel em `position: absolute` — sem
 * participar do fluxo, nada disso acima se aplica. Precisa de mockup e QA
 * próprios.
 */
.conv-sidebar-collapse--closed {
  display: none;
}

.conv-sidebar-collapse__inner {
  display: flex;
  min-width: 0;
  overflow: hidden;
}
</style>
